import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type {
  Id,
} from "@constructionautomation/backend/convex/_generated/dataModel";
import {
  ArrowLeft,
  Eye,
  Loader2,
  Search,
  ScanSearch,
  SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";

import { RunStatusBadge } from "@/components/run-status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { DocumentViewportHost } from "@/features/document-viewport/document-viewport-host";
import { getDocumentMediaKind } from "@/features/document-viewport/media";
import {
  DocumentViewportPageControls,
  DocumentViewportToolbar,
  DocumentViewportToolbarSection,
} from "@/features/document-viewport/toolbar";
import type { DocumentSource } from "@/features/document-viewport/types";
import { cn } from "@/lib/utils";
import { DocumentTagEditorOverlay } from "@/features/document-intelligence/document-tag-editor-overlay";
import { useRecordActionLog } from "@/hooks/use-action-log-session";

type RevisionRecord = {
  _id: Id<"projectDocumentRevisions">;
  revisionNumber: number;
  revisionName: string;
  originalFilename: string;
  mimeType?: string;
};

type DetectionRecord = {
  _id: Id<"documentIntelligenceDetections">;
  pageNumber: number;
  rawText: string;
  normalizedText: string;
  classification: "finish_tag" | "room_tag" | "door_window_tag" | "reference_noise" | "unknown";
  bbox: { x: number; y: number; width: number; height: number };
  confidence?: number;
  isUserManaged?: boolean;
  createdByUserId?: Id<"users">;
};
type RunRecord = {
  _id: Id<"documentIntelligenceRuns">;
  status: "pending" | "processing" | "completed" | "failed";
  error?: string;
  indexedPageCount?: number;
  pageCount?: number;
};

const CLASSIFICATION_OPTIONS = [
  { value: "all", label: "All classes" },
  { value: "finish_tag", label: "Finish tags" },
  { value: "room_tag", label: "Room tags" },
  { value: "door_window_tag", label: "Door / window" },
  { value: "reference_noise", label: "Reference noise" },
  { value: "unknown", label: "Unknown" },
] as const;

const CONFIDENCE_OPTIONS = [
  { value: "0", label: "Any confidence" },
  { value: "0.5", label: "50%+" },
  { value: "0.7", label: "70%+" },
  { value: "0.85", label: "85%+" },
] as const;

const CLASSIFICATION_LABELS: Record<DetectionRecord["classification"], string> = {
  finish_tag: "Finish",
  room_tag: "Room",
  door_window_tag: "Door/Window",
  reference_noise: "Reference",
  unknown: "Unknown",
};

const CLASSIFICATION_STYLES: Record<
  DetectionRecord["classification"],
  { border: string; fill: string; badge: string }
> = {
  finish_tag: {
    border: "border-emerald-500/90",
    fill: "bg-emerald-500/15 text-emerald-100",
    badge: "bg-emerald-500/15 text-emerald-200 border-emerald-400/40",
  },
  room_tag: {
    border: "border-sky-500/90",
    fill: "bg-sky-500/15 text-sky-100",
    badge: "bg-sky-500/15 text-sky-200 border-sky-400/40",
  },
  door_window_tag: {
    border: "border-amber-500/90",
    fill: "bg-amber-500/15 text-amber-100",
    badge: "bg-amber-500/15 text-amber-200 border-amber-400/40",
  },
  reference_noise: {
    border: "border-rose-500/90",
    fill: "bg-rose-500/15 text-rose-100",
    badge: "bg-rose-500/15 text-rose-200 border-rose-400/40",
  },
  unknown: {
    border: "border-violet-500/90",
    fill: "bg-violet-500/15 text-violet-100",
    badge: "bg-violet-500/15 text-violet-200 border-violet-400/40",
  },
};

function confidenceLabel(confidence: number | undefined) {
  if (confidence === undefined) {
    return "n/a";
  }
  return `${Math.round(confidence * 100)}%`;
}

interface DocumentIntelligenceViewportToolbarProps {
  activePage: number;
  pageCount: number;
  detectionCount: number;
  overlaysVisible: boolean;
  setOverlaysVisible: (value: boolean) => void;
  searchText: string;
  setSearchText: (value: string) => void;
  classification: string;
  setClassification: (value: string) => void;
  minConfidence: string;
  setMinConfidence: (value: string) => void;
  indexStatus: RunRecord["status"] | undefined;
  indexedPageCount: number | undefined;
  totalPageCount: number | undefined;
}

function DocumentIntelligenceViewportToolbar({
  activePage,
  pageCount,
  detectionCount,
  overlaysVisible,
  setOverlaysVisible,
  searchText,
  setSearchText,
  classification,
  setClassification,
  minConfidence,
  setMinConfidence,
  indexStatus,
  indexedPageCount,
  totalPageCount,
}: DocumentIntelligenceViewportToolbarProps) {
  return (
    <>
      <DocumentViewportToolbar className="gap-2 p-3">
          <DocumentViewportToolbarSection className="min-w-[180px]">
            <Eye className="size-4 text-muted-foreground" />
            <div className="flex items-center gap-2">
              <Switch
                checked={overlaysVisible}
                onCheckedChange={setOverlaysVisible}
                size="sm"
                aria-label="Show indexed tags"
              />
              <span className="text-sm font-medium">Show indexed tags</span>
            </div>
          </DocumentViewportToolbarSection>

          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchText}
              onChange={(event) => setSearchText(event.currentTarget.value)}
              placeholder="Filter detected text"
              className="pl-9"
              disabled={!overlaysVisible}
            />
          </div>

          <div className="flex min-w-[180px] items-center gap-2">
            <SlidersHorizontal className="size-4 text-muted-foreground" />
            <Select
              value={classification}
              onValueChange={setClassification}
              disabled={!overlaysVisible}
            >
              <SelectTrigger className="min-w-[180px] bg-background/70">
                <SelectValue placeholder="Filter class" />
              </SelectTrigger>
              <SelectContent>
                {CLASSIFICATION_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Select
            value={minConfidence}
            onValueChange={setMinConfidence}
            disabled={!overlaysVisible}
          >
            <SelectTrigger className="min-w-[160px] bg-background/70">
              <SelectValue placeholder="Confidence" />
            </SelectTrigger>
            <SelectContent>
              {CONFIDENCE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <DocumentViewportPageControls
            activePage={activePage}
            pageCount={pageCount}
            centerLabel={
              <div className="min-w-20 text-center text-sm font-medium tabular-nums text-muted-foreground">
                {activePage}/{pageCount}
              </div>
            }
          />
      </DocumentViewportToolbar>

      <div className="pointer-events-none absolute bottom-4 left-4 z-20 flex max-w-[calc(100%-2rem)] flex-wrap items-center gap-2">
        <Badge variant="outline" className="bg-background/95 backdrop-blur">
          Page {activePage} · {detectionCount} visible tag{detectionCount === 1 ? "" : "s"}
        </Badge>
        {indexStatus === "completed" && indexedPageCount !== undefined && totalPageCount !== undefined ? (
          <Badge variant="outline" className="bg-background/95 backdrop-blur">
            Indexed {indexedPageCount}/{totalPageCount} page{totalPageCount === 1 ? "" : "s"}
          </Badge>
        ) : null}
        {CLASSIFICATION_OPTIONS.filter((option) => option.value !== "all").map((option) => {
          const classification = option.value as DetectionRecord["classification"];
          const style = CLASSIFICATION_STYLES[classification];
          return (
            <span
              key={option.value}
              className={cn(
                "rounded-md border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]",
                style.badge
              )}
            >
              {CLASSIFICATION_LABELS[classification]}
            </span>
          );
        })}
      </div>
    </>
  );
}

interface DocumentIntelligenceViewerProps {
  projectName: string;
  documentName: string;
  revisions: RevisionRecord[];
  onBack: () => void;
}

interface ViewerIndexActionState {
  label: string;
  disabled: boolean;
  isReindex: boolean;
}

interface ViewerNoticeState {
  message: string;
  tone?: "default" | "destructive";
}

function getIndexActionState(
  latestRunStatus: RunRecord["status"] | undefined,
  hasSelectedRevision: boolean
): ViewerIndexActionState {
  return {
    label:
      latestRunStatus === "failed" || latestRunStatus === "completed"
        ? "Re-index"
        : latestRunStatus === "processing" || latestRunStatus === "pending"
          ? "Indexing"
          : "Index document",
    disabled:
      !hasSelectedRevision ||
      latestRunStatus === "processing" ||
      latestRunStatus === "pending",
    isReindex: latestRunStatus === "failed" || latestRunStatus === "completed",
  };
}

function getViewerNoticeState(args: {
  latestRun: RunRecord | null | undefined;
  overlaysVisible: boolean;
  isPageDetectionsPending: boolean;
  pageDetectionCount: number;
}): ViewerNoticeState | null {
  const { latestRun, overlaysVisible, isPageDetectionsPending, pageDetectionCount } = args;
  if (latestRun?.status === "failed") {
    return {
      message:
        latestRun.error ?? "The latest indexing run failed. Retry indexing to refresh tags.",
      tone: "destructive",
    };
  }
  if (latestRun?.status !== "completed") {
    return {
      message:
        latestRun?.status === "processing" || latestRun?.status === "pending"
          ? "Tag indexing is running for this revision. The document remains viewable while the index completes."
          : "Index this revision to surface detected tags directly in the viewer.",
    };
  }
  if (overlaysVisible && !isPageDetectionsPending && pageDetectionCount === 0) {
    return {
      message: "No indexed tags match the current filters on this page.",
    };
  }
  return null;
}

function useIndexDocumentAction(args: {
  selectedRevision: RevisionRecord | null;
  latestRunStatus: RunRecord["status"] | undefined;
  ensureIndex: (args: {
    revisionId: Id<"projectDocumentRevisions">;
  }) => Promise<{ reused: boolean; status: RunRecord["status"] }>;
  reindexDocument: (args: {
    revisionId: Id<"projectDocumentRevisions">;
  }) => Promise<{ reused: boolean; status: RunRecord["status"] }>;
  onAction?: () => void | Promise<void>;
}) {
  const { selectedRevision, latestRunStatus, ensureIndex, reindexDocument, onAction } = args;

  return useCallback(async () => {
    if (!selectedRevision) {
      return;
    }

    const isReindex = latestRunStatus === "completed" || latestRunStatus === "failed";

    try {
      const result = isReindex
        ? await reindexDocument({ revisionId: selectedRevision._id })
        : await ensureIndex({ revisionId: selectedRevision._id });

      if (result.reused) {
        toast.success(
          result.status === "completed"
            ? "Document is already indexed"
            : "Document indexing is already in progress"
        );
        return;
      }

      toast.success(isReindex ? "Document re-indexing started" : "Document indexing started");
      await onAction?.();
    } catch (error) {
      console.error(error);
      toast.error(
        isReindex
          ? "Failed to start document re-indexing"
          : "Failed to start document indexing"
      );
    }
  }, [ensureIndex, latestRunStatus, onAction, reindexDocument, selectedRevision]);
}

function LoadingOrErrorState({ downloadUrl }: { downloadUrl: string | null | undefined }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {downloadUrl === undefined ? (
        <Loader2 className="size-5 animate-spin" />
      ) : (
        "Unable to load this revision."
      )}
    </div>
  );
}

function ViewerFloatingNotice({
  children,
  tone = "default",
}: {
  children: string;
  tone?: "default" | "destructive";
}) {
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 flex justify-center px-4">
      <div
        className={cn(
          "pointer-events-auto max-w-xl rounded-xl border bg-background/95 px-4 py-3 text-sm shadow-lg backdrop-blur",
          tone === "destructive"
            ? "border-destructive/40 text-destructive"
            : "text-muted-foreground"
        )}
      >
        {children}
      </div>
    </div>
  );
}

function DocumentIntelligenceViewerHeader({
  onBack,
  documentName,
  projectName,
  selectedRevisionId,
  setSelectedRevisionId,
  sortedRevisions,
  latestRunStatus,
  handleIndexDocument,
  handleCancelRun,
  isCancellingRun,
  indexActionState,
}: {
  onBack: () => void;
  documentName: string;
  projectName: string;
  selectedRevisionId: string;
  setSelectedRevisionId: (value: string) => void;
  sortedRevisions: RevisionRecord[];
  latestRunStatus: RunRecord["status"] | undefined;
  handleIndexDocument: () => void;
  handleCancelRun: () => void;
  isCancellingRun: boolean;
  indexActionState: ViewerIndexActionState;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-background px-4 py-3 shadow-sm">
      <div className="min-w-0">
        <Button
          type="button"
          variant="link"
          onClick={onBack}
          className="mb-1 h-auto px-0 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground no-underline transition hover:text-foreground hover:no-underline"
        >
          <ArrowLeft className="size-3.5" />
          Back to Project Documents
        </Button>
        <div className="truncate text-lg font-semibold">{documentName}</div>
        <div className="text-sm text-muted-foreground">{projectName}</div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={selectedRevisionId} onValueChange={setSelectedRevisionId}>
          <SelectTrigger className="min-w-[320px] bg-background">
            <SelectValue placeholder="Select revision" />
          </SelectTrigger>
          <SelectContent>
            {sortedRevisions.map((revision) => (
              <SelectItem key={revision._id} value={String(revision._id)}>
                {`${revision.revisionName} (r${revision.revisionNumber}) · ${revision.originalFilename}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <RunStatusBadge status={latestRunStatus} />

        <Button
          type="button"
          variant={latestRunStatus === "failed" ? "destructive" : "outline"}
          onClick={handleIndexDocument}
          disabled={indexActionState.disabled}
        >
          {latestRunStatus === "processing" || latestRunStatus === "pending" ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <ScanSearch className="size-4" />
          )}
          {indexActionState.label}
        </Button>

        {latestRunStatus === "processing" || latestRunStatus === "pending" ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button type="button" variant="destructive" disabled={isCancellingRun}>
                {isCancellingRun ? <Loader2 className="size-4 animate-spin" /> : null}
                Cancel run
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Cancel indexing run?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will mark the current indexing run as failed so you can retry. The
                  service may still be working in the background, but its later writes will
                  be rejected.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep running</AlertDialogCancel>
                <AlertDialogAction onClick={handleCancelRun}>
                  Cancel run
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : null}
      </div>
    </div>
  );
}

export function DocumentIntelligenceViewer({
  projectName,
  documentName,
  revisions,
  onBack,
}: DocumentIntelligenceViewerProps) {
  const recordActionLog = useRecordActionLog();
  const ensureIndex = useMutation(api.documentIntelligence.runs.ensureForRevision);
  const reindexDocument = useMutation(api.documentIntelligence.runs.reindexForRevision);
  const cancelRun = useMutation(api.documentIntelligence.runs.cancel);
  const sortedRevisions = useMemo(
    () =>
      revisions
        .slice()
        .sort((a, b) =>
          b.revisionNumber - a.revisionNumber || String(b._id).localeCompare(String(a._id))
        ),
    [revisions]
  );
  const [selectedRevisionId, setSelectedRevisionId] = useState<string>(
    String(sortedRevisions[0]?._id ?? "")
  );
  const [activePage, setActivePage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [showIndexedTags, setShowIndexedTags] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [classification, setClassification] = useState<string>("all");
  const [minConfidence, setMinConfidence] = useState<string>("0");
  const [isCancellingRun, setIsCancellingRun] = useState(false);
  const deferredSearchText = useDeferredValue(searchText);

  useEffect(() => {
    setSelectedRevisionId((current) => {
      if (sortedRevisions.length === 0) {
        return "";
      }
      if (current && sortedRevisions.some((revision) => String(revision._id) === current)) {
        return current;
      }
      return String(sortedRevisions[0]?._id ?? "");
    });
  }, [sortedRevisions]);

  const selectedRevision = useMemo(
    () =>
      sortedRevisions.find((revision) => String(revision._id) === selectedRevisionId) ?? null,
    [selectedRevisionId, sortedRevisions]
  );

  useEffect(() => {
    setActivePage(1);
  }, [selectedRevisionId]);

  const { data: downloadUrl } = useQuery(
    convexQuery(
      api.projectDocuments.getRevisionDownloadUrl,
      selectedRevision ? { revisionId: selectedRevision._id } : "skip"
    )
  );
  const latestRunQuery = useQuery(
    convexQuery(
      api.documentIntelligence.runs.getLatestByRevision,
      selectedRevision ? { revisionId: selectedRevision._id } : "skip"
    )
  );

  const latestRun = latestRunQuery.data as RunRecord | null | undefined;
  const overlaysVisible = showIndexedTags && Boolean(selectedRevision);
  const pageDetectionsQuery = useQuery(
    convexQuery(
      api.documentIntelligence.tags.listByRevisionPage,
      selectedRevision && showIndexedTags
        ? {
            revisionId: selectedRevision._id,
            pageNumber: activePage,
            classification:
              classification === "all"
                ? undefined
                : (classification as DetectionRecord["classification"]),
            searchText: deferredSearchText.trim() || undefined,
            minConfidence:
              minConfidence === "0" ? undefined : Number.parseFloat(minConfidence),
          }
        : "skip"
    )
  );
  const pageTagData = pageDetectionsQuery.data as
    | { activeDetections: DetectionRecord[]; archivedDetections: DetectionRecord[] }
    | undefined;
  const pageDetections = pageTagData?.activeDetections ?? [];
  const archivedDetections = pageTagData?.archivedDetections ?? [];

  const source = useMemo<DocumentSource | null>(() => {
    if (!selectedRevision || !downloadUrl) {
      return null;
    }
    return {
      revisionId: String(selectedRevision._id),
      fileUrl: downloadUrl,
      filename: selectedRevision.originalFilename,
      mimeType: selectedRevision.mimeType,
      mediaKind: getDocumentMediaKind(
        selectedRevision.originalFilename,
        selectedRevision.mimeType
      ),
    };
  }, [downloadUrl, selectedRevision]);

  const handleIndexDocument = useIndexDocumentAction({
    selectedRevision,
    latestRunStatus: latestRun?.status,
    ensureIndex,
    reindexDocument,
    onAction: recordActionLog,
  });
  const handleCancelRun = useCallback(async () => {
    if (!latestRun || isCancellingRun) {
      return;
    }

    setIsCancellingRun(true);
    try {
      const result = await cancelRun({ runId: latestRun._id });
      if (!result.canceled) {
        toast.error("This indexing run can no longer be canceled");
        return;
      }
      toast.success("Indexing run canceled");
      await recordActionLog();
    } catch (error) {
      console.error(error);
      toast.error("Failed to cancel indexing run");
    } finally {
      setIsCancellingRun(false);
    }
  }, [cancelRun, isCancellingRun, latestRun, recordActionLog]);
  const indexActionState = getIndexActionState(latestRun?.status, Boolean(selectedRevision));
  const viewerNotice = getViewerNoticeState({
    latestRun,
    overlaysVisible,
    isPageDetectionsPending: pageDetectionsQuery.isPending,
    pageDetectionCount: pageDetections.length,
  });

  if (sortedRevisions.length === 0) {
    return (
      <div className="flex h-[calc(100vh-8rem)] items-center justify-center rounded-xl border bg-background text-sm text-muted-foreground">
        This document has no revisions yet.
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] min-h-[720px] flex-col gap-4">
      <DocumentIntelligenceViewerHeader
        onBack={onBack}
        documentName={documentName}
        projectName={projectName}
        selectedRevisionId={selectedRevisionId}
        setSelectedRevisionId={setSelectedRevisionId}
        sortedRevisions={sortedRevisions}
        latestRunStatus={latestRun?.status}
        handleIndexDocument={() => void handleIndexDocument()}
        handleCancelRun={() => void handleCancelRun()}
        isCancellingRun={isCancellingRun}
        indexActionState={indexActionState}
      />

      <div className="relative min-h-0 flex-1 overflow-hidden rounded-2xl border bg-background shadow-sm">
        {source ? (
          <DocumentViewportHost
            source={source}
            activePage={activePage}
            onActivePageChange={setActivePage}
            onPageCountChange={setPageCount}
            viewportPadding={{
              top: 104,
              right: 24,
              bottom: 28,
              left: 24,
            }}
            className="absolute inset-0 overflow-hidden"
          >
            {showIndexedTags && selectedRevision ? (
              <DocumentTagEditorOverlay
                revisionId={selectedRevision._id}
                activeDetections={pageDetections}
                archivedDetections={archivedDetections}
                getStyle={(detection) => ({
                  color:
                    detection.classification === "finish_tag"
                      ? "#10B981"
                      : detection.classification === "room_tag"
                        ? "#0EA5E9"
                        : detection.classification === "door_window_tag"
                          ? "#F59E0B"
                          : detection.classification === "reference_noise"
                            ? "#F43F5E"
                            : "#8B5CF6",
                  fillClassName: CLASSIFICATION_STYLES[detection.classification].fill,
                  badgeText: `${CLASSIFICATION_LABELS[detection.classification]} · ${confidenceLabel(detection.confidence)}`,
                })}
                showInlineLabel
                secondaryLabel={(detection) =>
                  `${CLASSIFICATION_LABELS[detection.classification]} · ${confidenceLabel(detection.confidence)}`
                }
                onAction={recordActionLog}
              />
            ) : null}
            <DocumentIntelligenceViewportToolbar
              activePage={activePage}
              pageCount={pageCount}
              detectionCount={pageDetections.length}
              overlaysVisible={showIndexedTags}
              setOverlaysVisible={setShowIndexedTags}
              searchText={searchText}
              setSearchText={setSearchText}
              classification={classification}
              setClassification={setClassification}
              minConfidence={minConfidence}
              setMinConfidence={setMinConfidence}
              indexStatus={latestRun?.status}
              indexedPageCount={latestRun?.indexedPageCount}
              totalPageCount={latestRun?.pageCount}
            />
          </DocumentViewportHost>
        ) : (
          <LoadingOrErrorState downloadUrl={downloadUrl} />
        )}

        {viewerNotice ? (
          <ViewerFloatingNotice tone={viewerNotice.tone}>
            {viewerNotice.message}
          </ViewerFloatingNotice>
        ) : null}

        {pageDetectionsQuery.isPending && overlaysVisible ? (
          <div className="pointer-events-none absolute right-4 top-24 z-20 rounded-md border bg-background/95 px-3 py-2 text-xs text-muted-foreground shadow-sm backdrop-blur">
            <div className="flex items-center gap-2">
              <Loader2 className="size-3.5 animate-spin" />
              Loading indexed tags
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
